<template>
  <!-- PC：7 列多组 -->
  <div class="video-layout video-layout__pc">
    <div class="video-group" v-for="(group, idx) in videoGroups" :key="'pc-' + idx">
      <div
        class="video-container"
        v-for="(video, index) in group"
        :key="idx + '-' + index"
        v-intersect="inView => handleVideoInView(`${idx}-${index}`, inView)"
        :class="video.size"
        @mouseenter="handleMouseEnter(idx, index)"
        @mouseleave="handleMouseLeave(idx, index)"
      >
        <div class="video-placeholder" :class="[video.size]">
          <img :src="video.poster" alt="Video poster" class="poster-image" />
        </div>
        <video
          :ref="el => setVideoRef(el as HTMLVideoElement, `${idx}-${index}`)"
          class="video-item"
          :class="[video.size]"
          :src="shouldLoadVideo(`${idx}-${index}`) ? video.src : undefined"
          :controls="false"
          :loop="true"
          :muted="true"
          :autoplay="false"
          preload="none"
        />
      </div>
    </div>
  </div>
  <!-- 移动端：5 卡网格，仅展示静态封面图，不播放视频 -->
  <div
    class="video-layout video-layout__mobile"
    v-intersect="{
      handler: inView => handleMobileModuleInView(inView),
      options: { threshold: 0.1, rootMargin: '100px 0px 100px 0px' },
    }"
  >
    <div
      v-for="(video, posIndex) in mobileGridCards"
      :key="'mobile-' + posIndex"
      class="video-container video-container--mobile"
      :class="video.size"
      v-intersect="inView => handleVideoInView(`m-${posIndex}`, inView)"
    >
      <div class="video-placeholder" :class="[video.size]">
        <img :src="video.poster" alt="Video poster" class="poster-image" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getFeUrl } from '~/utils/url';

interface VideoItem {
  src: string;
  size:
    | 'size-md-rect'
    | 'size-small-square'
    | 'size-small-rect'
    | 'size-square'
    | 'size-large-rect';
  poster: string;
}
const videos: VideoItem[] = [
  /* 移动端右上角：需为「棕色/米色衣服女性、头向右侧」；当前素材若不符请替换 OSS 上 1.webm/1.png */
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/1.webm'),
    size: 'size-md-rect',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/1.png?x-oss-process=image/format,webp',
    ),
  },
  /* 移动端右下角：需为「棕色/米色衣服女性、头向右侧」；当前素材若不符请替换 OSS 上 2.webm/2.png */
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/2.webm'),
    size: 'size-small-square',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/2.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/3.webm'),
    size: 'size-small-rect',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/3.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/4.webm'),
    size: 'size-square',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/4.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/5.webm'),
    size: 'size-square',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/5.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/6.webm'),
    size: 'size-large-rect',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/6.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/7.webm'),
    size: 'size-square',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/7.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/8.webm'),
    size: 'size-square',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/8.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/9.webm'),
    size: 'size-small-square',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/9.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/10.webm'),
    size: 'size-small-rect',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/10.png?x-oss-process=image/format,webp',
    ),
  },
  {
    src: getFeUrl('/youyan-platform/character-intro/avatar-matrix/11.webm'),
    size: 'size-md-rect',
    poster: getFeUrl(
      '/youyan-platform/character-intro/avatar-matrix/11.png?x-oss-process=image/format,webp',
    ),
  },
];

// 按指定的分组数量分割视频数组
const groupSizes = [1, 2, 2, 1, 2, 2, 1];
const videoGroups = ref<VideoItem[][]>([]);

/** 移动端 5 卡网格顺序：左上、中间大卡、右上、左下、右下；右上 7、右下 8（两位棕色衣服女性） */
const mobileGridOrder = [3, 5, 6, 4, 7];
const mobileGridCards = computed(() => mobileGridOrder.map(i => videos[i]));

const handleMobileModuleInView = async (inView: boolean) => {
  // 现阶段：移动端仅展示静态封面图，不播放视频
  if (!inView) {
    currentHover.value = null;
  }
};

// 当前悬停的视频key
const currentHover = ref<string | null>(null);

// 视频元素引用管理
const videoRefs = ref<Record<string, HTMLVideoElement>>({});

// 已进入视口的视频key集合
const loadedVideoKeys = ref<Set<string>>(new Set());

// 分组逻辑
let currentIndex = 0;
groupSizes.forEach(size => {
  const group = videos.slice(currentIndex, currentIndex + size);
  videoGroups.value.push(group);
  currentIndex += size;
});

// 处理视频进入视口
const handleVideoInView = (key: string, inView: boolean) => {
  if (inView) {
    loadedVideoKeys.value.add(key);
  }
};

// 判断是否应该加载视频源
const shouldLoadVideo = (key: string) => {
  return loadedVideoKeys.value.has(key);
};

// 设置视频元素引用 - 只在初始化时调用一次
const setVideoRef = (el: HTMLVideoElement | null, key: string) => {
  if (el && !videoRefs.value[key]) {
    videoRefs.value[key] = el;
  }
};

// 处理鼠标移入事件
const handleMouseEnter = async (groupIndex: number, videoIndex: number) => {
  const key = `${groupIndex}-${videoIndex}`;
  // console.log('Mouse enter:', key)

  currentHover.value = key;

  await nextTick();

  const videoElement = videoRefs.value[key];
  if (videoElement) {
    try {
      videoElement.currentTime = 0;
      await videoElement.play();
    } catch {
      // Ignore autoplay rejection and keep the poster visible.
    }
  }
};

// 处理鼠标移出事件
const handleMouseLeave = (groupIndex: number, videoIndex: number) => {
  const key = `${groupIndex}-${videoIndex}`;
  // console.log('Mouse leave:', key)

  const videoElement = videoRefs.value[key];
  if (videoElement) {
    videoElement.pause();
    videoElement.currentTime = 0;
  }

  currentHover.value = null;
};

// 组件卸载时清理
onUnmounted(() => {
  Object.values(videoRefs.value).forEach(video => {
    if (video) video.pause();
  });
});
</script>
<style lang="scss" scoped src="./styles/video-matrix-pc.scss"></style>
<style lang="scss" scoped src="./styles/video-matrix-mobile.scss"></style>
